import { useEffect, useRef, useState } from 'react';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { cn } from '@/lib/utils';

interface ProgressBarProps {
	loading: boolean;
}

const ProgressBar = ({ loading }: ProgressBarProps) => {
	const [isVisible, setIsVisible] = useState(false);
	const [isHidden, setIsHidden] = useState(true);
	const { appData } = useAppContext();
	const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const progress = Math.round(
		((appData?.quotes?.sick_leave_short_term?.length || 0) / (appData?.expected_num_quotes || 1)) *
			100
	);

	useEffect(() => {
		if (!loading) {
			// Loading stopped - clear any pending show timer and start fade out if visible
			if (showTimerRef.current) {
				clearTimeout(showTimerRef.current);
				showTimerRef.current = null;
			}

			if (isVisible) {
				const timer = setTimeout(() => {
					setIsVisible(false);
					setTimeout(() => {
						setIsHidden(true);
					}, 500);
				}, 500);
				return () => clearTimeout(timer);
			}
		} else {
			// Loading started - wait 2 seconds before showing to avoid flashing
			showTimerRef.current = setTimeout(() => {
				setIsVisible(true);
				setIsHidden(false);
			}, 2000);
		}

		return () => {
			if (showTimerRef.current) {
				clearTimeout(showTimerRef.current);
				showTimerRef.current = null;
			}
		};
	}, [loading, isVisible]);

	if (isHidden) return null;

	return (
		<>
			{(appData?.expected_num_quotes || 0) > 1 && (
				<div
					data-testid="progressbar"
					className={cn(
						'mb-6 transition-opacity duration-500',
						isVisible ? 'opacity-100' : 'opacity-0'
					)}
				>
					<div
						className="w-full bg-muted rounded-full h-3"
						role="progressbar"
						aria-label="Quote progress"
						aria-valuenow={progress}
						aria-valuemin={0}
						aria-valuemax={100}
					>
						<div
							className={cn(
								'bg-primary h-3 rounded-full transition-all duration-300',
								loading && 'animate-pulse'
							)}
							style={{ width: `${progress}%` }}
						/>
					</div>
				</div>
			)}
		</>
	);
};

export default ProgressBar;
